import { useState, useEffect } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, HelpCircle, Lightbulb, ArrowUpRight, Store, Download } from "lucide-react";
import {
  ETAPAS_DATA,
  ETAPAS_ORDER,
  ACCENTS,
  CONTEUDOS_POR_ETAPA,
  FORNECEDORES_DESTAQUE,
} from "@/data/etapasData";
import Seo from "@/components/Seo";
import { getJSON, accountSend } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChecklistDownloadModal, { downloadChecklistPdf } from "@/components/ChecklistDownloadModal";
import GuiaSaude from "@/components/GuiaSaude";
import { toast } from "sonner";

const MotionLink = motion.create(Link);

const Section = ({ children, className = "", ...rest }) => (
  <section className={`py-20 md:py-24 ${className}`} {...rest}>
    <div className="max-w-7xl mx-auto px-6 md:px-12">{children}</div>
  </section>
);

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const EtapaPage = () => {
  const slug = useLocation().pathname.replace(/^\//, "");
  const data = ETAPAS_DATA[slug];
  const { user } = useAuth();
  const [checked, setChecked] = useState({});
  const [artigos, setArtigos] = useState([]);
  const [guia, setGuia] = useState(null);
  const [showDownload, setShowDownload] = useState(false);
  const isFamilia = user && user.role === "familia";

  useEffect(() => {
    const nome = ETAPAS_DATA[slug]?.nome;
    // progresso salvo na conta (mãe) ou reset para visitantes
    const saved = isFamilia ? (user.checklist_progress?.[slug] || []) : [];
    const init = {};
    saved.forEach((i) => { init[i] = true; });
    setChecked(init);
    if (!nome) return;
    getJSON("/conteudos")
      .then((all) => setArtigos(all.filter((x) => x.etapa === nome).slice(0, 3)))
      .catch(() => setArtigos([]));
    getJSON(`/checklist/${slug}/guia`).then(setGuia).catch(() => setGuia(null));
  }, [slug, isFamilia, user]);

  if (!data) return <Navigate to="/" replace />;

  const a = ACCENTS[data.accent];
  const conteudos = CONTEUDOS_POR_ETAPA[slug] || [];
  const fornecedores = FORNECEDORES_DESTAQUE[slug] || [];

  const idx = ETAPAS_ORDER.indexOf(slug);
  const prev = idx > 0 ? ETAPAS_DATA[ETAPAS_ORDER[idx - 1]] : null;
  const next = idx < ETAPAS_ORDER.length - 1 ? ETAPAS_DATA[ETAPAS_ORDER[idx + 1]] : null;

  const toggle = (i) => {
    setChecked((c) => {
      const nextState = { ...c, [i]: !c[i] };
      if (isFamilia) {
        const arr = Object.keys(nextState).filter((k) => nextState[k]).map(Number).sort((x, y) => x - y);
        accountSend(`/account/checklist/${slug}`, "PUT", { checked: arr }).catch(() => {});
      }
      return nextState;
    });
  };

  const baixarChecklist = async () => {
    if (user) {
      try { await downloadChecklistPdf(slug); toast.success("Download iniciado."); }
      catch (e) { toast.error(e.message); }
    } else {
      setShowDownload(true);
    }
  };

  return (
    <main data-testid="etapa-page" className="bg-white">
      <Seo
        title={`${data.nome} — Etapa ${data.numero}`}
        path={`/${slug}`}
        description={data.heroSubtitle || data.descricao}
        image={data.heroImg}
      />
      {/* HERO */}
      <section data-testid="etapa-hero" className={`${a.heroBg} relative overflow-hidden`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
              <div className="flex items-center gap-3 mb-5 text-sm">
                <Link to="/" className="text-maternnia-taupe hover:text-maternnia-charcoal transition-colors">Início</Link>
                <span className="text-maternnia-taupe/50">/</span>
                <span className={`font-semibold ${a.text}`}>Etapa {data.numero} • {data.nome}</span>
              </div>
              <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-maternnia-charcoal font-medium">
                {data.heroTitle}
              </h1>
              <p className="mt-6 text-base md:text-lg text-maternnia-taupe leading-relaxed font-light max-w-xl">
                {data.heroSubtitle}
              </p>
              <a
                href="/#cadastro"
                data-testid="etapa-hero-cta"
                className={`group mt-9 inline-flex items-center gap-2 ${a.bg} ${a.bgHover} text-white rounded-full px-8 py-4 text-base font-medium transition-all duration-300 shadow-sm hover:shadow-lg hover:scale-[1.03] active:scale-[0.98]`}
              >
                Receber conteúdos desta etapa
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
              <div className={`rounded-[2rem] overflow-hidden shadow-[0_28px_64px_rgba(43,37,38,0.14)] ${data.heroPortrait ? "aspect-[4/5] max-w-sm mx-auto lg:ml-auto" : "aspect-[4/3]"}`}>
                <img src={data.heroImg} alt={data.heroAlt} className={`w-full h-full object-cover ${data.imgPos || ""}`} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTERNAL NAV between etapas */}
      <div className="sticky top-24 z-30 bg-white/85 backdrop-blur-xl border-y border-maternnia-nevoa">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 overflow-x-auto py-3 no-scrollbar">
            {ETAPAS_ORDER.map((s) => {
              const e = ETAPAS_DATA[s];
              const active = s === slug;
              return (
                <Link
                  key={s}
                  to={`/${s}`}
                  data-testid={`etapa-nav-${s}`}
                  className={`whitespace-nowrap text-sm font-medium rounded-full px-5 py-2 transition-all duration-300 ${
                    active ? "bg-maternnia-charcoal text-white" : "text-maternnia-taupe hover:bg-maternnia-porcelana"
                  }`}
                >
                  {e.nome}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* DESCRIÇÃO */}
      <Section data-testid="etapa-descricao">
        <motion.div className="max-w-3xl" {...reveal}>
          <span className={`text-sm font-semibold tracking-widest uppercase ${a.text}`}>Sobre esta etapa</span>
          <p className="mt-5 font-serif text-2xl md:text-3xl text-maternnia-charcoal leading-snug font-light">
            {data.descricao}
          </p>
          <div className="flex flex-wrap gap-2 mt-8">
            {data.temasChips.map((t) => (
              <span key={t} className={`text-xs font-medium rounded-full px-4 py-2 ${a.soft} ${a.softText}`}>{t}</span>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* TRIMESTRES (gestação) */}
      {data.trimestres && (
        <Section data-testid="etapa-trimestres" className="bg-maternnia-porcelana">
          <motion.h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium mb-12" {...reveal}>
            Organizado por trimestre
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.trimestres.map((t, i) => (
              <motion.div key={t.titulo} className="bg-white rounded-3xl p-8 border border-maternnia-nevoa/60 shadow-sm" {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}>
                <span className={`font-serif text-3xl ${a.text}`}>{i + 1}º</span>
                <h3 className="font-serif text-2xl text-maternnia-charcoal mt-1 mb-2 font-medium">{t.titulo}</h3>
                <p className="text-sm text-maternnia-taupe leading-relaxed font-light">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* DÚVIDAS + DECISÕES */}
      <Section data-testid="etapa-duvidas">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.div {...reveal}>
            <div className="flex items-center gap-2 mb-7">
              <HelpCircle size={20} className={a.text} />
              <h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium">Principais dúvidas</h2>
            </div>
            <div className="space-y-4">
              {data.duvidas.map((d, i) => (
                <div key={i} data-testid={`duvida-${i}`} className="bg-maternnia-porcelana rounded-2xl p-6 border border-maternnia-nevoa/50">
                  <h4 className="font-medium text-maternnia-charcoal">{d.q}</h4>
                  <p className="mt-2 text-sm text-maternnia-taupe leading-relaxed font-light">{d.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...reveal}>
            <div className="flex items-center gap-2 mb-7">
              <Lightbulb size={20} className={a.text} />
              <h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium">Decisões importantes</h2>
            </div>
            <div className="space-y-4">
              {data.decisoes.map((d, i) => (
                <div key={i} data-testid={`decisao-${i}`} className="flex gap-4 items-start">
                  <span className={`mt-1 h-8 w-8 rounded-xl ${a.soft} ${a.softText} flex items-center justify-center text-sm font-semibold shrink-0`}>{i + 1}</span>
                  <div>
                    <h4 className="font-medium text-maternnia-charcoal">{d.title}</h4>
                    <p className="mt-1 text-sm text-maternnia-taupe leading-relaxed font-light">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </Section>

      {/* CHECKLIST */}
      <Section data-testid="etapa-checklist" className="bg-maternnia-nevoa/40">
        <motion.div className="max-w-3xl mx-auto" {...reveal}>
          <h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium text-center mb-3">
            Checklist da etapa
          </h2>
          <p className="text-center text-sm text-maternnia-taupe font-light mb-10">
            {isFamilia ? "Marque o que já está resolvido — seu progresso fica salvo na sua conta." : "Marque o que já está resolvido e baixe o checklist para acompanhar em qualquer lugar."}
          </p>
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-maternnia-nevoa/60 space-y-2">
            {data.checklist.map((item, i) => (
              <button
                key={i}
                data-testid={`checklist-item-${i}`}
                onClick={() => toggle(i)}
                className="w-full flex items-center gap-4 text-left p-3 rounded-xl hover:bg-maternnia-porcelana/60 transition-colors"
              >
                <span className={`h-6 w-6 rounded-md border flex items-center justify-center shrink-0 transition-all duration-200 ${checked[i] ? `${a.bg} border-transparent` : "border-maternnia-nevoa bg-white"}`}>
                  {checked[i] && <Check size={14} className="text-white" />}
                </span>
                <span className={`text-sm transition-colors ${checked[i] ? "text-maternnia-taupe/70 line-through" : "text-maternnia-charcoal"}`}>{item}</span>
              </button>
            ))}
          </div>
          <div className="mt-6 text-center">
            <button
              data-testid="etapa-baixar-checklist"
              onClick={baixarChecklist}
              className={`inline-flex items-center gap-2 ${a.bg} ${a.bgHover} text-white rounded-full px-7 py-3.5 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md hover:scale-[1.03] active:scale-[0.98]`}
            >
              <Download size={17} /> Baixar guia premium em PDF
            </button>
            {!user && (
              <p className="mt-3 text-xs text-maternnia-taupe font-light">
                É rápido: deixe seu contato e receba o guia completo em PDF.{" "}
                <Link to="/entrar" className="text-maternnia-eucalipto underline underline-offset-2">Ou crie uma conta</Link> para salvar seu progresso.
              </p>
            )}
          </div>
        </motion.div>
      </Section>

      {/* GUIA OFICIAL DE SAÚDE (dados oficiais) */}
      <GuiaSaude guia={guia} a={a} />

      {/* CONTEÚDOS RELACIONADOS */}
      <Section data-testid="etapa-conteudos">
        <motion.h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium mb-10" {...reveal}>
          Conteúdos relacionados
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {artigos.map((c, i) => (
            <MotionLink
              key={c.id}
              to={`/conteudos/${c.slug}`}
              data-testid={`etapa-conteudo-${i}`}
              className="group block bg-white rounded-3xl overflow-hidden border border-maternnia-nevoa/60 shadow-sm hover:shadow-[0_16px_40px_rgba(43,37,38,0.08)] hover:-translate-y-1 transition-all duration-300"
              {...reveal}
              transition={{ ...reveal.transition, delay: i * 0.08 }}
            >
              <div className="h-40 overflow-hidden">
                <img src={c.imagem} alt={c.imagemAlt || c.titulo} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-7">
                <span className={`text-xs font-semibold tracking-widest uppercase ${a.text}`}>{c.categoria || data.nome}</span>
                <h3 className="mt-2 font-serif text-xl text-maternnia-charcoal leading-snug font-medium group-hover:text-maternnia-eucalipto transition-colors">{c.titulo}</h3>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-maternnia-taupe font-light">Leitura de {c.tempoLeitura}</span>
                  <ArrowUpRight size={17} className={`${a.text} transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1`} />
                </div>
              </div>
            </MotionLink>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/conteudos" data-testid="etapa-ver-todos-conteudos" className={`inline-flex items-center gap-2 text-sm font-medium ${a.text} hover:gap-3 transition-all`}>
            Ver todos os conteúdos <ArrowRight size={16} />
          </Link>
        </div>
      </Section>

      {/* CATEGORIAS DE PROFISSIONAIS */}
      <Section data-testid="etapa-categorias" className="bg-maternnia-porcelana">
        <motion.div {...reveal}>
          <h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium mb-3">Profissionais para esta etapa</h2>
          <p className="text-sm text-maternnia-taupe font-light mb-8">Categorias mais procuradas para o seu momento.</p>
          <div className="flex flex-wrap gap-3">
            {data.categorias.map((c) => (
              <Link
                key={c}
                to="/#profissionais"
                data-testid={`etapa-categoria-${c}`}
                className="text-sm font-medium text-maternnia-charcoal bg-white border border-maternnia-nevoa rounded-full px-5 py-2.5 hover:bg-maternnia-eucalipto hover:text-white hover:border-maternnia-eucalipto transition-all duration-300"
              >
                {c}
              </Link>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* FORNECEDORES EM DESTAQUE */}
      <Section data-testid="etapa-fornecedores">
        <motion.h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium mb-10" {...reveal}>
          Fornecedores em destaque
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {fornecedores.map((f, i) => (
            <motion.div
              key={i}
              data-testid={`etapa-fornecedor-${i}`}
              className="bg-white rounded-3xl p-7 border border-maternnia-nevoa/60 shadow-sm hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(43,37,38,0.08)] transition-all duration-300"
              {...reveal}
              transition={{ ...reveal.transition, delay: i * 0.08 }}
            >
              <span className={`h-12 w-12 rounded-2xl ${a.soft} flex items-center justify-center mb-5`}>
                <Store size={22} className={a.text} />
              </span>
              <h3 className="font-serif text-2xl text-maternnia-charcoal font-medium">{f.nome}</h3>
              <p className="mt-1 text-sm text-maternnia-taupe font-light">{f.tipo}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA CADASTRO */}
      <section data-testid="etapa-cta" className={`${a.bg}`}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24 text-center">
          <motion.div {...reveal}>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-medium tracking-tight">
              Receba um checklist para a etapa {data.nome}
            </h2>
            <p className="mt-4 text-white/85 font-light max-w-xl mx-auto">
              Cadastre-se gratuitamente e receba conteúdos e materiais selecionados para o seu momento.
            </p>
            <a
              href="/#cadastro"
              data-testid="etapa-cta-button"
              className="group mt-9 inline-flex items-center gap-2 bg-white text-maternnia-charcoal hover:bg-maternnia-porcelana rounded-full px-8 py-4 text-base font-medium transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              Quero receber gratuitamente
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* PREV / NEXT */}
      <Section data-testid="etapa-prevnext">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {prev ? (
            <Link to={`/${prev.slug}`} data-testid="etapa-prev" className="group flex items-center gap-4 bg-maternnia-porcelana rounded-2xl p-6 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
              <ArrowLeft size={20} className="text-maternnia-taupe transition-transform duration-300 group-hover:-translate-x-1" />
              <div>
                <p className="text-xs text-maternnia-taupe uppercase tracking-wide">Etapa anterior</p>
                <p className="font-serif text-xl text-maternnia-charcoal font-medium">{prev.nome}</p>
              </div>
            </Link>
          ) : <div className="hidden sm:block" />}
          {next && (
            <Link to={`/${next.slug}`} data-testid="etapa-next" className="group flex items-center justify-end gap-4 bg-maternnia-porcelana rounded-2xl p-6 text-right hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
              <div>
                <p className="text-xs text-maternnia-taupe uppercase tracking-wide">Próxima etapa</p>
                <p className="font-serif text-xl text-maternnia-charcoal font-medium">{next.nome}</p>
              </div>
              <ArrowRight size={20} className="text-maternnia-taupe transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
        </div>
      </Section>

      <ChecklistDownloadModal open={showDownload} onClose={() => setShowDownload(false)} slug={slug} etapaNome={data.nome} />
    </main>
  );
};

export default EtapaPage;
